import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Stack,
    Table,
    Typography,
} from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../context";
import { APIGetBlockedProfiles, APIToggleBlockProfile } from "../api";
import { BlockRounded, LockOpenRounded, PersonRounded } from "@mui/icons-material";


export default function BlockedProfilesPage() {
    const { currentDoctor, token } = useContext(LayoutContext);

    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unblocking, setUnblocking] = useState({});

    const hasAccess =
        currentDoctor?.type_profile?.includes('admin') ||
        currentDoctor?.type_profile?.includes('moderator') ||
        currentDoctor?.type_profile?.includes('supervisor');

    const load = async () => {
        if (!hasAccess) return;
        try {
            setLoading(true);
            setError(null);
            const data = await APIGetBlockedProfiles(token);
            setProfiles(data.profiles || []);
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось загрузить список');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [token]);

    const handleUnblock = async (profile) => {
        if (!window.confirm(`Разблокировать профиль "${profile.name}"?`)) return;
        try {
            setUnblocking(prev => ({ ...prev, [profile.id]: true }));
            const t = Array.isArray(profile.type_profile) ? profile.type_profile[0] : profile.type_profile;
            await APIToggleBlockProfile(token, profile.id, t || 'patient', '');
            setProfiles(prev => prev.filter(p => p.id !== profile.id));
        } catch (e) {
            setError(e?.response?.data?.error || 'Ошибка при разблокировке');
        } finally {
            setUnblocking(prev => ({ ...prev, [profile.id]: false }));
        }
    };

    if (!hasAccess) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert color="danger">Раздел доступен только администратору и модератору</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{
            px: { xs: 2, md: 6 }, pt: 2, pb: 3,
            display: 'flex', flexDirection: 'column', gap: 2,
        }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography level="h2" component="h1" startDecorator={<BlockRounded color="danger" />}>
                    Заблокированные профили
                </Typography>
                <Chip color="neutral" variant="soft">{profiles.length}</Chip>
            </Stack>

            {error && <Alert color="danger">{error}</Alert>}

            <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : profiles.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="neutral">Нет заблокированных профилей</Typography>
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table hoverRow sx={{ '--TableCell-paddingY': '0.7rem' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>ID</th>
                                    <th>Имя</th>
                                    <th>Телефон</th>
                                    <th>Тип</th>
                                    <th>Причина</th>
                                    <th>Кем</th>
                                    <th>Когда</th>
                                    <th style={{ width: 160 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {profiles.map(p => (
                                    <tr key={p.id}>
                                        <td>{p.id}</td>
                                        <td>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PersonRounded sx={{ fontSize: 16, opacity: 0.6 }} />
                                                <Typography level="body-sm" fontWeight="md">{p.name}</Typography>
                                            </Stack>
                                        </td>
                                        <td>{p.phone || '—'}</td>
                                        <td>
                                            {(Array.isArray(p.type_profile) ? p.type_profile : []).map(t => (
                                                <Chip key={t} size="sm" variant="soft" sx={{ mr: 0.3 }}>{t}</Chip>
                                            ))}
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>
                                                {p.block_reason || <i style={{ opacity: 0.5 }}>не указана</i>}
                                            </Typography>
                                        </td>
                                        <td>{p.blocked_by?.name || '—'}</td>
                                        <td>
                                            <Typography level="body-xs">
                                                {p.blocked_at ? new Date(p.blocked_at).toLocaleString('ru-RU') : '—'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                color="success"
                                                loading={!!unblocking[p.id]}
                                                startDecorator={<LockOpenRounded />}
                                                onClick={() => handleUnblock(p)}
                                            >
                                                Разблокировать
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Box>
                )}
            </Card>
        </Box>
    );
}
